import type React from 'react';
import type { ReactElement, ReactNode } from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';
import getConfig from 'next/config';

import { Broadcast } from 'shared/page/Broadcast';
import { useUnleash } from '@/hooks/unleash';

import type { PaleState } from '@/interfaces';
import { FilterGroup } from './filters/FilterGroup';
import PaginationLinks from './pagination/Pagination';
import ActivityFilter from './filters/checkbox/ActivityFilter';
import FacilityFilter from './filters/checkbox/FacilityFilter';
import KeywordFilter from './KeywordFilter';
import MealFilter from './filters/checkbox/MealFilter';
import CharterSupplierFilter from './filters/checkbox/CharterSupplierFilter';
import NonCharterSupplierFilter from './filters/checkbox/NonCharterSupplierFilter';
import HotelRatingFilter from './filters/checkbox/HotelRatingFilter';
import ResultTravelTimePicker from './filters/travelTime/ResultTravelTimePicker';
import ResultOriginDropdown from './filters/origin/ResultOriginDropdown';
import DestinationFilter from './filters/checkbox/DestinationFilter';
import { EditPanel } from './filters/EditPanel';
import ResultFilterPreview from './filters/ResultFilterPreview';
import DestinationDropdown from './filters/destination/DestinationDropdown';
import { DesktopTopBar } from './topBar/DesktopTopBar';
import MobileTopBar from './topBar/MobileTopBar';
import DestinationList from './filters/destination/DestinationList';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import PriceSlider from '@/components/filters/slider/PriceSlider';
import DistanceToBeachSlider from '@/components/filters/slider/DistanceToBeachSlider';
import DistanceToCitySlider from '@/components/filters/slider/DistanceToCitySlider';

const {
    publicRuntimeConfig: { broadcastFullUrl },
} = getConfig();

interface Props {
    title?: string;
    filtersOpen: boolean;
    children: React.ReactNode;
    desktop?: boolean;
}

const Layout: React.FC<Props> = ({ children, filtersOpen }): ReactElement<Props> => {
    const unleash = useUnleash();

    const renderOriginDestinationTimePanel = (): ReactNode => (
        <EditPanel preview={<ResultFilterPreview />} className="u-pa0--gt768 u-mt32--lt768 u-mb16--lt768">
            <FilterGroup title="Destinasjon" testId="Destinasjon" showOnMobile={false}>
                <div className="edit-panel-hotelfilters finn-reise--pakkereiser-hotell" data-testid="hotelFilter">
                    <ResultOriginDropdown />
                    <DestinationDropdown />
                    <DestinationList />
                    <h3 className="u-screen-reader-only">Alternativer for avreise og hjemreise</h3>
                    <ResultTravelTimePicker />
                </div>
            </FilterGroup>
        </EditPanel>
    );

    const renderOriginTimeFilterGroup = (): ReactNode => (
        <>
            <FilterGroup title="Reis fra" testId="origin">
                <ResultOriginDropdown hideLabel={true} />
                <h3 className="u-screen-reader-only">Alternativer for avreise og hjemreise</h3>
                <ResultTravelTimePicker />
            </FilterGroup>
            <FilterGroup title="Reisemål" testId="destination">
                <DestinationFilter />
            </FilterGroup>
        </>
    );

    return (
        <>
            <Head>
                <title>Pakkereiser - sammenlign pris | FINN reise</title>
                <meta property="og:title" content="Pakkereiser - sammenlign pris | FINN reise" />
                <meta property="og:url" content="https://www.finn.no/reise/pakkereiser/resultat" />
                <link rel="canonical" href="https://www.finn.no/reise/pakkereiser/resultat" />
            </Head>
            <main>
                <div className="pageholder u-m-mt32" data-replace-main-section>
                    <Broadcast url={broadcastFullUrl} troika />
                    <div>
                        <div className="u-hide-lt768">
                            <DesktopTopBar />
                        </div>
                        <div className="panel grid u-mt4">
                            <div className={'grid__unit u-r-size1of3'}>
                                <div className="u-pr8--gt768">{unleash.destinationAutocomplete && renderOriginDestinationTimePanel()}</div>
                                <div className="u-hide-gt768">
                                    <MobileTopBar />
                                </div>
                                <div className={`u-pr8 pp-filters-overlay ${filtersOpen ? 'pp-filters-overlay-active' : ''}`}>
                                    {!unleash.destinationAutocomplete && renderOriginTimeFilterGroup()}
                                    <FilterGroup title="Pris per person" testId="pricePerPerson">
                                        <PriceSlider />
                                    </FilterGroup>
                                    <FilterGroup title="Hotellstandard" testId="hotelStandard">
                                        <HotelRatingFilter />
                                    </FilterGroup>
                                    <FilterGroup title="Avstander" testId="distances">
                                        <DistanceToBeachSlider />
                                        <DistanceToCitySlider />
                                    </FilterGroup>
                                    <FilterGroup title="Fasiliteter og tilbud" testId="facilitiesAndOffers">
                                        <MealFilter />
                                        <FacilityFilter />
                                        <ActivityFilter />
                                        <KeywordFilter />
                                    </FilterGroup>
                                    <FilterGroup title="Tilbydere og selskaper" testId="providers">
                                        <CharterSupplierFilter />
                                        <NonCharterSupplierFilter />
                                    </FilterGroup>
                                </div>
                            </div>
                            <div className={`grid__unit u-r-size2of3 ${filtersOpen ? 'u-display-none' : ''}`}>
                                {unleash.currentFilters && <CurrentFilters />}
                                {children}
                                <PaginationLinks />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

const mapStateToProps = (state: PaleState): Pick<Props, 'filtersOpen'> => ({
    filtersOpen: state.appState.filtersOpen,
});

export default connect(mapStateToProps)(Layout);
